'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useLang } from '@/lib/langContext';
import { t } from '@/lib/i18n';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface SignUpValues {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Props {
  onSwitchToSignIn: () => void;
}

export default function SignUpForm({ onSwitchToSignIn }: Props) {
  const { lang } = useLang();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpValues>();
  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpValues) => {
    setLoading(true);
    const supabase = createClient();

    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: { full_name: data.fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error || !authData?.user) {
      setLoading(false);
      toast.error(error?.message || 'Failed to create account. Please try again.');
      return;
    }

    // Create workspace for the new user
    const { data: workspace, error: wsError } = await supabase
      .from('workspaces')
      .insert({
        name: `${data.fullName}'s Workspace`,
        owner_user_id: authData.user.id,
        industry_mode: 'real_estate',
      })
      .select()
      .single();

    if (!wsError && workspace) {
      // Add user as owner in workspace_members
      await supabase.from('workspace_members').insert({
        workspace_id: workspace.id,
        user_id: authData.user.id,
        role: 'owner',
      });
    }

    setLoading(false);
    toast.success('Account created! Setting up your workspace…');
    router.push('/onboarding');
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const supabase = createClient();
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    setGoogleLoading(false);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))] mb-2">{t(lang, 'signUp')}</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          {t(lang, 'alreadyHaveAccount')}{' '}
          <button onClick={onSwitchToSignIn} className="text-navy-600 font-medium hover:underline">
            {t(lang, 'signIn')}
          </button>
        </p>
      </div>

      <button
        onClick={handleGoogle}
        disabled={googleLoading}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-btn border border-[hsl(var(--border))] bg-white text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] active:scale-[0.99] transition-all duration-150 mb-4"
      >
        {googleLoading ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        {t(lang, 'continueWithGoogle')}
      </button>

      <div className="flex items-center gap-3 mb-4">
        <hr className="flex-1 border-[hsl(var(--border))]" />
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{t(lang, 'orContinueWith')}</span>
        <hr className="flex-1 border-[hsl(var(--border))]" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">{t(lang, 'fullName')}</label>
          <input
            type="text"
            autoComplete="name"
            placeholder="Your full name"
            {...register('fullName', { required: t(lang, 'nameRequired') })}
            className={`w-full px-3 py-2.5 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
              ${errors.fullName ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
          />
          {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">{t(lang, 'email')}</label>
          <input
            type="email"
            autoComplete="email"
            placeholder="you@developer.ae"
            {...register('email', {
              required: t(lang, 'emailRequired'),
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t(lang, 'emailInvalid') },
            })}
            className={`w-full px-3 py-2.5 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
              ${errors.email ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">{t(lang, 'password')}</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('password', {
                required: t(lang, 'passwordRequired'),
                minLength: { value: 8, message: t(lang, 'passwordMin') },
              })}
              className={`w-full px-3 py-2.5 pe-10 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
                ${errors.password ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
            />
            <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 end-0 flex items-center pe-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">{t(lang, 'confirmPassword')}</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: t(lang, 'passwordRequired'),
                validate: v => v === passwordValue || t(lang, 'passwordMismatch'),
              })}
              className={`w-full px-3 py-2.5 pe-10 rounded-btn border text-sm bg-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-navy-400 focus:ring-offset-1
                ${errors.confirmPassword ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))] hover:border-navy-200'}`}
            />
            <button type="button" onClick={() => setShowConfirm(s => !s)} className="absolute inset-y-0 end-0 flex items-center pe-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>}
        </div>

        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          By creating an account you agree to our{' '}
          <a href="#" className="text-navy-600 hover:underline">Terms of Service</a> and{' '}
          <a href="#" className="text-navy-600 hover:underline">Privacy Policy</a>.
        </p>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-btn bg-navy-600 text-white text-sm font-semibold hover:bg-navy-700 active:scale-[0.99] transition-all duration-150 disabled:opacity-70"
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              {t(lang, 'creatingAccount')}
            </>
          ) : t(lang, 'signUp')}
        </button>
      </form>
    </div>
  );
}